import {
  Button,
  makeStyles,
  Portal,
  shorthands,
  tokens,
  useRestoreFocusTarget,
} from "@fluentui/react-components";
import {
  Dismiss12Regular,
  QuestionCircle24Regular,
  QuestionCircleRegular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { activeDialogSurface, HELP_Z, isStepOnScreen } from "./dom";
import { HelpDrawer } from "./HelpDrawer";
import { HelpInspect } from "./HelpInspect";
import { HelpTooltip } from "./HelpTooltip";
import { HelpTour } from "./HelpTour";
import { readFloatingPref, writeFloatingPref } from "./preference";
import { helpForPath } from "./registry";
import type { HelpLanguage, HelpStep, HelpViewer } from "./types";
import { localizedContext, localizedContexts, localizedPage, stepVisibleForRole } from "./types";

/**
 * The entry point to the page guide — two of them, in fact:
 *
 *  - a DOCKED "?" in the top bar's right cluster (`GrecoHeader`), next to the notifications bell.
 *    Always there. With the floating "?" off it stays clickable over an open dialog, through the
 *    portaled copy below.
 *  - a FLOATING "?" in the bottom-right corner. Opt-in (see ./preference), because that is where
 *    the tables keep their pagination and row actions. When on, it is the one that stays
 *    clickable over a dialog, and the docked copy is not drawn.
 *
 * `pathname`, the viewer's role flags, the language and the toast hook-up arrive as props rather
 * than being read from the router, react-query, i18next or react-toastify in here, so `help/`
 * stays importable from a test or a Node script with no providers around it.
 */

/** A message the guide wants shown to the user, outside its own panels. */
export interface HelpNotice {
  severity: "info" | "warning" | "success";
  message: string;
  /** One button on the toast — the "Undo" after hiding the floating button. */
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

/**
 * The drawer's close motion (Fluent `OverlayDrawer`, size "small" → durationGentle, 250 ms).
 * Starting the tour sooner would spotlight elements still under the sliding panel.
 */
const DRAWER_CLOSE_MS = 280;

/** Same glyph size as the header's other icon buttons (the book and the bell). */
const DOCKED_ICON_SIZE = 24;

const useStyles = makeStyles({
  /**
   * The portal's own mount node. Fluent gives every portal container `z-index: 1000000` and a
   * stacking context, so the z-index on the button inside counts only within it — a dialog
   * opened AFTER the floating "?" mounted landed on top of it and swallowed its clicks.
   */
  portalLayer: {
    zIndex: HELP_Z.portal,
  },
  // Sized like the header's other icon buttons (large, shrunk to the icon) so the row lines up.
  docked: { color: "inherit", minWidth: "fit-content" },
  dockedActive: { color: tokens.colorBrandForeground1, minWidth: "fit-content" },
  dockedHidden: {
    visibility: "hidden",
  },
  elevated: {
    position: "fixed",
    zIndex: HELP_Z.launcherCopy,
    minWidth: "fit-content",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  floatingWrap: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    zIndex: HELP_Z.launcherCopy,
    // The dismiss badge is a hover affordance — always-on it would read as part of the button.
    ":hover .help-fab-dismiss": {
      opacity: 1,
      pointerEvents: "auto",
    },
    ":focus-within .help-fab-dismiss": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  fab: {
    boxShadow: tokens.shadow16,
  },
  fabDismiss: {
    position: "absolute",
    top: "-6px",
    right: "-6px",
    minWidth: "22px",
    width: "22px",
    height: "22px",
    ...shorthands.padding(0),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.border("2px", "solid", tokens.colorNeutralBackground1),
    // Dark on purpose: a light badge disappears into the page behind it.
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralForegroundInverted,
    opacity: 0,
    pointerEvents: "none",
    transitionProperty: "opacity",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: tokens.colorNeutralForeground1,
      color: tokens.colorNeutralForegroundInverted,
    },
  },
});

/**
 * Whether a modal dialog is on screen right now. Watched rather than measured once, because the
 * elevated "?" has to appear the moment a dialog takes the screen over and go away when it
 * closes. State is set only on change — the observer hears every table re-render.
 */
const useDialogOpen = (): boolean => {
  const [open, setOpen] = useState(false);
  const last = useRef(false);

  useEffect(() => {
    const read = (): void => {
      const next = activeDialogSurface() !== null;
      if (next === last.current) return;
      last.current = next;
      setOpen(next);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return open;
};

interface HelpLauncherProps {
  pathname: string;
  viewer: HelpViewer;
  /** The guide language — follows the app's language setting (`helpLanguageFor`). */
  language: HelpLanguage;
  /** The app's toast system. */
  notify: (notice: HelpNotice) => void;
  /** The colour of the header's other icons, so the docked "?" matches them. */
  iconColor?: string;
}

export const HelpLauncher = ({
  pathname,
  viewer,
  language,
  notify,
  iconColor,
}: HelpLauncherProps) => {
  const styles = useStyles();
  // Keyed to the route rather than a boolean: navigating away closes the panel by definition.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [tour, setTour] = useState<{
    steps: HelpStep[];
    scope: HTMLElement | null;
    /** Which of them to open on — a reader who picked a field does not start at step 1. */
    startIndex: number;
  } | null>(null);
  /** "Point at a field": the page stays visible and inert while the user points at controls. */
  const [inspecting, setInspecting] = useState(false);
  const [floating, setFloating] = useState(readFloatingPref);
  const dialogOpen = useDialogOpen();
  /**
   * Whether the docked "?" hands over to its copy above the dialog. Not while the floating "?"
   * is on: that one already stays clickable over a dialog, and a second "?" drawn over the
   * dialog's own header (a side panel's title, say) only gets in the way.
   */
  const elevate = dialogOpen && !floating;
  /** Where the docked button sits, so the copy that outranks a dialog can stand on it. */
  const dockRef = useRef<HTMLButtonElement>(null);
  const [dockRect, setDockRect] = useState<DOMRect | null>(null);
  /** The drawer is a Fluent dialog: on close it returns focus to the "?" that opened it. */
  const restoreFocusTarget = useRestoreFocusTarget();
  /** The delayed tour/inspector start — cancelled when the route changes underneath it. */
  const pendingStart = useRef(0);

  /**
   * A Fluent dialog's backdrop covers the top bar, so the docked "?" is unclickable the moment a
   * form opens. While a dialog is up the button is re-rendered as a portaled copy pinned to the
   * same rectangle, above the backdrop. Re-measured on resize.
   */
  useEffect(() => {
    if (!elevate) {
      setDockRect(null);
      return;
    }
    const measure = (): void => {
      const el = dockRef.current;
      if (el) setDockRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [elevate]);

  // A route change ends a tour or the inspector — they describe a screen the user has left —
  // and cancels one that was about to start with the previous page's steps.
  useEffect(() => {
    window.clearTimeout(pendingStart.current);
    setTour(null);
    setInspecting(false);
  }, [pathname]);
  useEffect(() => {
    const pending = pendingStart;
    return () => window.clearTimeout(pending.current);
  }, []);

  const help = helpForPath(pathname);

  const roleSteps = useMemo(
    () => (help ? help.steps.filter((step) => stepVisibleForRole(step, viewer)) : []),
    [help, viewer]
  );

  // An unregistered route has no guide and therefore no button — that is what lets the manual
  // roll out page by page.
  if (!help) return null;

  const title = localizedPage(help, language).title;
  const open = openFor === pathname;
  const toggle = (): void => setOpenFor(open ? null : pathname);

  const chooseFloating = (on: boolean): void => {
    setFloating(on);
    writeFloatingPref(on);
    if (!on) {
      notify({
        severity: "success",
        message:
          "Floating button hidden. The guide is still one click away: the “?” in the top bar.",
        action: { label: "Undo", onClick: () => chooseFloating(true) },
        durationMs: 7000,
      });
    }
  };

  // Hidden while the panel, a tour or the inspector is up: over the page being explained it is
  // one more thing in the way.
  const showFloating = floating && !open && tour === null && !inspecting;

  /** Open the tour — from the beginning, or at the step the reader picked. */
  const startTour = (from?: HelpStep): void => {
    setOpenFor(null);
    setInspecting(false);
    // Measured after the drawer has closed: the panel would otherwise cover the elements being
    // spotlighted.
    window.clearTimeout(pendingStart.current);
    pendingStart.current = window.setTimeout(() => {
      // A tour started over an open dialog stays INSIDE it — the page behind the backdrop
      // cannot be clicked, and the dialog is usually what the user opened the guide for.
      const surface = activeDialogSurface();
      // Role-visible AND actually rendered within that surface: the tour never spotlights a
      // hole where a dialog field or an empty-state button would be.
      const visible = roleSteps.filter((step) => isStepOnScreen(step, surface ?? document));
      if (visible.length === 0) {
        notify({
          severity: "info",
          message: surface
            ? "Nothing in this dialog is documented yet — close it to tour the page, or read the panel."
            : "Nothing to point at on this screen yet — the guide in the panel covers it all.",
        });
        return;
      }
      const startIndex = from ? visible.indexOf(from) : 0;
      if (from && startIndex < 0) {
        // The drawer lists the WHOLE guide, including controls behind a dialog or another view.
        // Say which and how to reach it, rather than silently starting somewhere else.
        const hint = from.context ? localizedContext(help, from.context, language) : undefined;
        notify({
          severity: "info",
          message: hint
            ? `“${from.title}” is not on screen yet. ${hint}`
            : `“${from.title}” is not on screen right now, so the tour cannot open on it.`,
        });
        return;
      }
      setTour({ steps: visible, scope: surface, startIndex: Math.max(startIndex, 0) });
    }, DRAWER_CLOSE_MS);
  };

  /** Hand over to "Point at a field". Same delay as the tour, for the same reason. */
  const startInspect = (): void => {
    setOpenFor(null);
    setTour(null);
    window.clearTimeout(pendingStart.current);
    pendingStart.current = window.setTimeout(() => setInspecting(true), DRAWER_CLOSE_MS);
  };

  return (
    <>
      <HelpTooltip content={`Page guide: ${title}`}>
        <Button
          ref={dockRef}
          size="large"
          appearance="transparent"
          icon={<QuestionCircleRegular fontSize={DOCKED_ICON_SIZE} />}
          aria-label={`Open the page guide for ${title}`}
          aria-pressed={open}
          data-help="app-help-launcher"
          onClick={toggle}
          className={elevate ? styles.dockedHidden : open ? styles.dockedActive : styles.docked}
          // The open state keeps the brand colour from its class.
          style={open ? undefined : { color: iconColor }}
          {...restoreFocusTarget}
          // While the elevated copy stands on top of it, this one is inert — leaving it in the
          // accessibility tree would announce the same button twice.
          {...(elevate ? { "aria-hidden": true, tabIndex: -1 } : {})}
        />
      </HelpTooltip>

      {/* Not while the guide itself is open: the copy outranks the drawer and would sit on it. */}
      {elevate && !open && (
        <Portal mountNode={{ className: styles.portalLayer }}>
          <HelpTooltip content={`Page guide: ${title}`}>
            <Button
              size="large"
              appearance="subtle"
              icon={<QuestionCircleRegular fontSize={DOCKED_ICON_SIZE} />}
              aria-label={`Open the page guide for ${title}`}
              onClick={toggle}
              className={styles.elevated}
              {...restoreFocusTarget}
              style={{
                ...(iconColor ? { color: iconColor } : {}),
                ...(dockRect
                  ? {
                      top: dockRect.top,
                      left: dockRect.left,
                      width: dockRect.width,
                      height: dockRect.height,
                    }
                  : // Only if the measurement failed: roughly where the top bar keeps it.
                    { top: 8, right: 140 }),
              }}
            />
          </HelpTooltip>
        </Portal>
      )}

      {showFloating && (
        <Portal mountNode={{ className: styles.portalLayer }}>
          <div className={styles.floatingWrap}>
            <HelpTooltip content={`Page guide: ${title}`} positioning="before">
              <Button
                appearance="primary"
                shape="circular"
                size="large"
                icon={<QuestionCircle24Regular />}
                aria-label="Open the page guide (floating shortcut)"
                onClick={toggle}
                className={styles.fab}
                {...restoreFocusTarget}
              />
            </HelpTooltip>
            <HelpTooltip content="Hide this — the guide stays in the top bar" positioning="above">
              <Button
                className={`help-fab-dismiss ${styles.fabDismiss}`}
                icon={<Dismiss12Regular />}
                aria-label="Hide the floating page-guide button"
                onClick={() => chooseFloating(false)}
              />
            </HelpTooltip>
          </div>
        </Portal>
      )}

      <HelpDrawer
        open={open}
        help={help}
        steps={roleSteps}
        floating={floating}
        onFloatingChange={chooseFloating}
        language={language}
        onClose={() => setOpenFor(null)}
        onStartTour={startTour}
        onInspect={startInspect}
        notify={notify}
      />

      {tour && (
        <HelpTour
          steps={tour.steps}
          scope={tour.scope}
          startIndex={tour.startIndex}
          sections={help.sections}
          help={help}
          language={language}
          onInspect={startInspect}
          onClose={() => setTour(null)}
        />
      )}

      {inspecting && (
        <HelpInspect
          steps={roleSteps}
          help={help}
          language={language}
          contexts={localizedContexts(help, language)}
          onTourFrom={startTour}
          onExit={() => setInspecting(false)}
        />
      )}
    </>
  );
};
